import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Database, Globe, Zap, Mail, Webhook } from "lucide-react";
import { FaWhatsapp, FaFacebook, FaGoogle, FaSalesforce } from "react-icons/fa";
import { RoundIcon } from "./components/cards";
import { useWarehouseMetrics } from "./warehouse-metrics-api";
import separationImage from "../../../assets/images/Central AI Core.png";
// "Connect Your Data Ecosystem" — the two-column Sources/Destinations card
// with a small connector badge between them.
const SOURCE_ICONS = [
  { icon: <FileText size={13} />, className: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  { icon: <FaWhatsapp size={13} />, className: "bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-400" },
  { icon: <Database size={13} />, className: "bg-orange-50 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400" },
  { icon: <FaGoogle size={13} />, className: "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  { icon: <FaSalesforce size={13} />, className: "bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  { icon: <Zap size={13} />, className: "bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400" },
];

const DESTINATION_ICONS = [
  { icon: <FileText size={13} />, className: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  { icon: <FaWhatsapp size={13} />, className: "bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-400" },
  { icon: <FaFacebook size={13} />, className: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" },
  { icon: <FaGoogle size={13} />, className: "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  { icon: <FaSalesforce size={13} />, className: "bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  { icon: <Mail size={13} />, className: "bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400" },
  { icon: <Webhook size={13} />, className: "bg-teal-50 dark:bg-teal-500/15 text-teal-600 dark:text-teal-400" },
];

const EcosystemColumn: React.FC<{
  icon: React.ReactNode;
  title: string;
  icons: typeof SOURCE_ICONS;
  description: string;
  count: number | string;
  countLabel: string;
  buttonLabel: string;
  onButtonClick: () => void;
  loading?: boolean;
}> = ({ icon, title, icons, description, count, countLabel, buttonLabel, onButtonClick, loading }) => (
  <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
      {icon}
      {title}
    </div>
    <div className="flex items-center gap-1.5 mt-3 flex-wrap">
      {icons.map((it, i) => (
        <RoundIcon key={i} icon={it.icon} className={it.className} />
      ))}
    </div>
    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">{description}</p>
    <div className="mt-4">
      {loading ? (
        <div className="h-6 w-10 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
      ) : (
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">{count}</div>
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400">{countLabel}</p>
    </div>
    <button
      type="button"
      onClick={onButtonClick}
      className="mt-4 px-3.5 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      {buttonLabel}
    </button>
  </div>
);

export const DataEcosystemSection: React.FC = () => {
  const navigate = useNavigate();
  // Sources/Destinations setup now lives under Convo Track — see
  // routes/main-routes.tsx's track/connections/new (reuses the same
  // connector flow the old cdp/connections/new route used).
  const goToNewConnection = () => navigate("/track/connections/new");

  // No single "total connected sources"/"total destinations" metric is
  // documented on GET /api/v1/dashboard/warehouse-metrics — only per-channel
  // rows under the connected_source/activated_destination groups (the same
  // ones channel-grid.tsx renders below). The count here is derived as how
  // many of those channels actually have data (metric_value > 0), not a
  // fabricated total of its own.
  const { loading, byGroup } = useWarehouseMetrics();
  const connectedSourceCount = byGroup("connected_source").filter((m) => m.metric_value > 0).length;
  const activatedDestinationCount = byGroup("activated_destination").filter((m) => m.metric_value > 0).length;

  return (
    <section>
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
        Connect Your Data Ecosystem
      </h2>
      <div className=" dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row items-stretch gap-6 lg:gap-0">
          <EcosystemColumn
            icon={<FileText size={16} className="text-gray-500 dark:text-gray-400" />}
            title="Connect Sources"
            icons={SOURCE_ICONS}
            description="Collect customer data from sources like CSV files, databases, CRM platforms, websites and messaging channels to start building unified customer profiles."
            count={connectedSourceCount}
            countLabel="Connected Sources"
            buttonLabel="Add Source"
            onButtonClick={goToNewConnection}
            loading={loading}
          />

          {/* Connector badge between the two columns */}
          <div className="hidden lg:flex items-center justify-center">
            <img src={separationImage} alt="Separation" className="flex items-center justify-center shadow-sm" />
          </div>

          <EcosystemColumn
            icon={<Globe size={16} className="text-gray-500 dark:text-gray-400" />}
            title="Activate Destinations"
            icons={DESTINATION_ICONS}
            description="Connect the destinations where you want to put your customer data to work. Add them to activate unified profiles and audiences across your business channels."
            count={activatedDestinationCount}
            countLabel="Destinations"
            buttonLabel="Add Destination"
            onButtonClick={goToNewConnection}
            loading={loading}
          />
        </div>
      </div>
    </section>
  );
};

export default DataEcosystemSection;
